import { stripExtraOppwaFieldsDelayed } from "./oppwaStripExtraFields";
import { layoutOppwaWidget, type LayoutOptions } from "./oppwaWidgetLayout";
import {
  normalizeOppwaBrand,
  type CardNetwork,
} from "./cardBrandUtils";

export const OPPWA_PAYMENT_IFRAME_NAME = "oppwaPaymentFrame";
export const OPPWA_PAYMENT_STARTED_EVENT = "oppwa-payment-started";

export type OppwaFocusField = "number" | "holder" | "expire" | "cvv" | null;

export type OppwaPreviewBridgeHandlers = {
  onNumber: (value: string) => void;
  onHolder: (value: string) => void;
  onMonth: (value: string) => void;
  onYear: (value: string) => void;
  onCvv: (value: string) => void;
  onFocus: (field: OppwaFocusField) => void;
  onBrand?: (brand: CardNetwork) => void;
  onWidgetReady?: (container: HTMLElement) => void;
};

let activeHandlers: OppwaPreviewBridgeHandlers | null = null;
let layoutOptions: LayoutOptions = {};
let shopperResultUrl = "";

export function setOppwaPreviewHandlers(handlers: OppwaPreviewBridgeHandlers | null) {
  activeHandlers = handlers;
}

export function setOppwaLayoutOptions(options: LayoutOptions) {
  layoutOptions = options;
}

export function setOppwaShopperResultUrl(url: string) {
  shopperResultUrl = url;
}

export function getOppwaPreviewHandlers() {
  return activeHandlers;
}

function iframeFieldFromContext(ctx: unknown): OppwaFocusField | null {
  const comm = ctx as { $iframe?: unknown };
  const raw = comm?.$iframe;
  const iframeEl =
    raw && typeof raw === "object" && raw !== null && "0" in raw
      ? (raw as Record<"0", HTMLElement>)["0"]
      : raw instanceof HTMLElement
      ? raw
      : null;

  if (!iframeEl) return null;
  const group = iframeEl.closest("[class*='wpwl-group-']");
  if (!group) return null;
  const cls = group.className.toLowerCase();
  if (cls.includes("cvv")) return "cvv";
  if (cls.includes("cardnumber")) return "number";
  if (cls.includes("expir")) return "expire";
  if (cls.includes("holder")) return "holder";
  return null;
}

function finalizeWidget(container: HTMLElement | null): boolean {
  if (!container) return false;
  if (container.dataset.oppwaFinalized === "true") return true;

  stripExtraOppwaFieldsDelayed(container);
  const laidOut = layoutOppwaWidget(container, layoutOptions);
  if (laidOut) container.dataset.oppwaFinalized = "true";
  return laidOut;
}

function attachSubmitGuard(container: HTMLElement): void {
  const form = container.querySelector("form.wpwl-form-card");
  if (!form || form.dataset.submitGuard === "true") return;
  form.dataset.submitGuard = "true";

  form.addEventListener(
    "submit",
    () => {
      container.dataset.paymentSubmitting = "true";
    },
    false
  );
}

/** wpwlOptions — payment logic unchanged; layout runs once on ready. */
export function buildOppwaPreviewWpwlOptions() {
  return {
    style: "plain" as const,
    showOneClickWidget: false,
    hideOtherPaymentButton: true,
    brandDetection: true,
    brandDetectionType: "binlist" as const,
    // Use _top so the OPPWA test / 3DS step is visible and can redirect back to our callback URL.
    paymentTarget: "_top",
    shopperResultTarget: "_top",
    ...(shopperResultUrl ? { shopperResultUrl } : {}),
    labels: {
      cardHolder: "Name on card",
      cardNumber: "Card Number",
      expiryDate: "Expiry date",
      cvv: "CVV",
    },
    placeholders: {
      cardHolder: "Name as shown on card",
      cardNumber: "1234 5678 9012 3456",
      expiryDate: "MM / YY",
      cvv: "•••",
    },
    onReady: () => {
      const container = document.querySelector(
        ".credit-card-form__widget-body"
      ) as HTMLElement | null;
      if (!finalizeWidget(container)) return;
      if (container) attachSubmitGuard(container);
      activeHandlers?.onWidgetReady?.(container!);
    },
    onDetectBrand: (_brands: string[], activeBrand?: string) => {
      activeHandlers?.onBrand?.(normalizeOppwaBrand(activeBrand));
    },
    onChangeBrand: (brand: string) => {
      activeHandlers?.onBrand?.(normalizeOppwaBrand(brand));
    },
    onFocusIframeCommunication: function (this: unknown) {
      const field = iframeFieldFromContext(this);
      if (field) activeHandlers?.onFocus(field);
    },
    onBlurIframeCommunication: function (this: unknown) {
      window.setTimeout(() => {
        const container = document.querySelector(".credit-card-form__widget-body");
        const active = document.activeElement;
        if (!container?.contains(active)) activeHandlers?.onFocus(null);
      }, 0);
    },
  };
}

export { finalizeWidget };
